import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatDeliveryMethod } from './utils/statusUtils';

interface SelectedPatient {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  dob?: string;
}

const formatBillingOption = (option: string) => {
  switch (option) {
    case 'patientPays': return 'Patient Pays';
    case 'doctorPays': return 'Doctor Pays';
    default: return option;
  }
};

interface CheckoutConfirmationProps {
  product: string;
  quantity: number;
  address: string;
  deliveryInfo?: any;
  productDirections?: any;
  dosage?: string;
  duration?: string;
  patients?: SelectedPatient[];
  strength?: string;
  size?: string;
  billingOption?: string;
  providerName?: string;
}

const CheckoutConfirmation: React.FC<CheckoutConfirmationProps> = ({ product, quantity, address, deliveryInfo, productDirections, dosage, duration, patients, strength, size, billingOption, providerName }) => {
  const [showPatients, setShowPatients] = useState(true);

  return (
    <Box sx={{ maxWidth: 600, margin: 'auto' }}>
      <Paper sx={{ padding: 3, marginBottom: 2 }}>
        <Typography variant="h4" gutterBottom color="primary">
          Order Confirmed!
        </Typography>
        <Typography variant="h6" gutterBottom>
          Thank you for your prescription order.
        </Typography>
        
        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Typography variant="h6" gutterBottom>
            Prescription Details:
          </Typography>
          {providerName && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Provider:</strong> {providerName}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Medication:</strong> {product}
          </Typography>
          {strength && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Strength:</strong> {strength}
            </Typography>
          )}
          {size && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Size:</strong> {size}
            </Typography>
          )}
          {dosage && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Dosage:</strong> {dosage}
            </Typography>
          )}
          {duration && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Duration:</strong> {duration}
            </Typography>
          )}
          {patients && patients.length > 0 ? (
            <>
              {patients.length > 1 ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      mb: 1,
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      borderRadius: 1,
                      p: 0.5,
                      ml: -0.5
                    }}
                    onClick={() => setShowPatients(!showPatients)}
                  >
                    <Typography variant="body1">
                      <strong>Patients Prescribed ({patients.length}):</strong>
                    </Typography>
                    <IconButton size="small" sx={{ ml: 0.5 }}>
                      {showPatients ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={showPatients}>
                    <Box sx={{ ml: 2, mb: 1 }}>
                      {patients.map((patient) => (
                        <Typography key={patient.id} variant="body2" sx={{ mb: 0.5 }}>
                          • {patient.name}
                        </Typography>
                      ))}
                    </Box>
                  </Collapse>
                </>
              ) : (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Patient Prescribed:</strong> {patients[0].name}
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{patients.length > 1 ? 'Quantity per patient:' : 'Quantity:'}</strong> {quantity}
              </Typography>
            </>
          ) : (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Quantity:</strong> {quantity}
            </Typography>
          )}
          {productDirections && (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Days of Supply:</strong> {productDirections.days}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Refills:</strong> {productDirections.refills}
              </Typography>
              {productDirections.directions && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Directions:</strong> {productDirections.directions}
                </Typography>
              )}
            </>
          )}
        </Box>

        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Typography variant="h6" gutterBottom>
            Delivery Information:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Method:</strong> {formatDeliveryMethod(deliveryInfo?.deliveryMethod || 'Delivery')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Address:</strong> {address}
          </Typography>
          {deliveryInfo?.isAsap ? (
            <Box sx={{ mb: 1 }}>
              <strong>Priority:</strong> <Chip label="ASAP" color="error" size="small" />
            </Box>
          ) : deliveryInfo?.needByDate ? (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Need By:</strong> {new Date(deliveryInfo.needByDate.includes('T') ? deliveryInfo.needByDate : deliveryInfo.needByDate + 'T12:00:00').toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          ) : null}
          {billingOption && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Billing Option:</strong> {formatBillingOption(billingOption)}
            </Typography>
          )}
        </Box>

        {deliveryInfo?.specialInstructions && (
          <Box sx={{ mt: 3, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>
              Order Special Instructions:
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {deliveryInfo.specialInstructions}
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ padding: 2, textAlign: 'left' }}>
        <Typography variant="h6" gutterBottom>
          What happens next?
        </Typography>
        <Typography variant="body1" paragraph>
          We will process the prescription and fill it per your instructions. We will contact you with any questions.
        </Typography>
        <Typography variant="body1">
          If you need any help, please call us at 718-472-0900.
        </Typography>
      </Paper>
    </Box>
  );
};

export default CheckoutConfirmation;
